import React, { PropsWithChildren, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import ErrorText from "../../components/errorText";
import * as yup from "yup";
import update from "immutability-helper";
import { Logger } from '../../utils/logger';
import GlobalDrawer from "../../components/GlobalDrawer";
import { toast } from "react-hot-toast";

interface WebhookData {
  webhookUrl: string;
  requestMethod: string;
  webhookServiceType?: string;
  reqBody: string;
  reqHeader: Record<string, string>;
  mapper: Record<string, string>;
}

interface HeaderEntry {
  key: string;
  value: string;
}

interface TeamMember {
  value: string;
  label: string;
  type: string;
}

interface Props {
  addUpdateModalValue: any;
  onSaveDrawerClose: (obj: any) => void;
}

const ActionWebhook: React.FC<PropsWithChildren<Props>> = ({
  addUpdateModalValue,
  onSaveDrawerClose,
}) => {
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [assignLeadDrawer, setAssignLeadDrawer] = useState(false);

  const StoreData = useSelector((state: any) => {
    return state?.rootReducers;
  });

  const teamMembers: TeamMember[] =
    StoreData?.userData?.userDetails?.userPreference?.customForm?.map(
      (item: any) => {
        return {
          value: item?.value,
          label: item?.name,
          type: item?.type,
        };
      }
    ) || [];

  const [webhookData, setWebhookData] = useState<WebhookData>({
    webhookUrl: "",
    requestMethod: "POST",
    webhookServiceType: "",
    reqBody: "",
    reqHeader: {
      "Content-Type": "application/json"
    },
    mapper: {}
  });

  // State for managing header key-value pairs
  const [headerEntries, setHeaderEntries] = useState<HeaderEntry[]>([
    { key: "Content-Type", value: "application/json" }
  ]);

  const [errors, setErrors] = useState({
    webhookUrl: "",
    reqBody: "",
    mapper: "",
  });

  useEffect(() => {
    if (Object.values(addUpdateModalValue).length > 0) {
      const data = addUpdateModalValue;

      // Reconstruct the template with {{field}} placeholders for editing
      let reqBodyString = "";
      if (data.reqBody) {
        if (typeof data.reqBody === 'object') {
          // If we have a mapper, reconstruct the template with placeholders
          if (data.mapper && Object.keys(data.mapper).length > 0) {
            reqBodyString = reconstructTemplate(data.reqBody, data.mapper);
          } else {
            // No mapper, just stringify the object
            reqBodyString = JSON.stringify(data.reqBody, null, 2);
          }
        } else {
          // Already a string
          reqBodyString = data.reqBody;
        }
      }

      // Convert reqHeader Record to HeaderEntry array
      const headers = data.reqHeader || { "Content-Type": "application/json" };
      const headerArray = Object.entries(headers).map(([key, value]) => ({
        key,
        value: String(value)
      }));
      setHeaderEntries(headerArray.length > 0 ? headerArray : [{ key: "Content-Type", value: "application/json" }]);

      setWebhookData({
        webhookUrl: data.webhookUrl || "",
        requestMethod: data.requestMethod || "POST",
        webhookServiceType: data.webhookServiceType || "",
        reqBody: reqBodyString,
        reqHeader: headers,
        mapper: data.mapper || {}
      });
    }
  }, [addUpdateModalValue]);

  let schema = yup.object().shape({
    webhookUrl: yup.string()
      .url("Invalid URL format")
      .matches(/^https:\/\//, "URL must use HTTPS")
      .required("Webhook URL is required"),
    reqBody: yup.string()
      .max(2000, "Max 2000 characters are allowed")
      .test('valid-fields', 'Invalid field reference in reqBody', function(value) {
        if (!value) return true; // Empty is okay

        // Extract all {{field}} patterns
        const matches = value.match(/\{\{(.*?)\}\}/g);
        if (!matches) return true; // No patterns is okay

        // Get list of valid field names
        const validFields = teamMembers.map(f => f.value);

        // Check each pattern
        for (const match of matches) {
          const fieldName = match.replace(/\{\{|\}\}/g, "").trim();
          if (!validFields.includes(fieldName)) {
            return this.createError({
              message: `Field "{{${fieldName}}}" not found in available lead fields. Available fields: ${validFields.join(', ')}`
            });
          }
        }
        return true;
      }),
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.currentTarget;
    setWebhookData({
      ...webhookData,
      [name]: value,
    });
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  // Header management functions
  const addHeaderEntry = () => {
    setHeaderEntries([...headerEntries, { key: "", value: "" }]);
  };

  const removeHeaderEntry = (index: number) => {
    const newEntries = headerEntries.filter((_, i) => i !== index);
    // Always keep at least one header entry
    setHeaderEntries(newEntries.length > 0 ? newEntries : [{ key: "Content-Type", value: "application/json" }]);
  };

  const updateHeaderEntry = (index: number, field: 'key' | 'value', value: string) => {
    const newEntries = [...headerEntries];
    newEntries[index][field] = value;
    setHeaderEntries(newEntries);
  };

  // Convert header entries array to Record<string, string>
  const headersToRecord = (): Record<string, string> => {
    const record: Record<string, string> = {};
    headerEntries.forEach(entry => {
      // Only add headers with both key and value
      if (entry.key.trim() && entry.value.trim()) {
        record[entry.key.trim()] = entry.value.trim();
      }
    });
    return record;
  };

  const insertLeadField = (item: TeamMember) => {
    const fieldText = `{{${item.value}}}`;
    const textarea = bodyRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const textBefore = webhookData.reqBody.substring(0, start);
    const textAfter = webhookData.reqBody.substring(end);
    const newValue = `${textBefore}${fieldText}${textAfter}`;

    if (newValue.length <= 2000) {
      setWebhookData({ ...webhookData, reqBody: newValue });
      setAssignLeadDrawer(false);

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(
          start + fieldText.length,
          start + fieldText.length
        );
      }, 0);
    } else {
      toast.error("Adding this field would exceed the 2000 character limit");
    }
  };

  // Auto-generate mapper with ONLY fields that are referenced in the reqBody
  const generateMapper = (text: string): Record<string, string> => {
    const mapper: Record<string, string> = {};

    // Extract only {{field}} patterns from reqBody
    const matches = text.match(/\{\{(.*?)\}\}/g);
    if (matches) {
      matches.forEach(match => {
        const fieldName = match.replace(/\{\{|\}\}/g, "").trim();
        // Find if this field exists in teamMembers (available lead fields)
        const field = teamMembers.find(f => f.value === fieldName);
        if (field) {
          // CRM field name (left) maps to itself as webhook variable (right)
          mapper[fieldName] = fieldName;
        } else {
          // For custom/dynamic fields not in teamMembers, map to itself
          mapper[fieldName] = fieldName;
        }
      });
    }

    return mapper;
  };

  // Reconstruct template with {{field}} placeholders from stored object and mapper
  const reconstructTemplate = (reqBodyObj: any, mapper: Record<string, string>): string => {
    if (!reqBodyObj || typeof reqBodyObj !== 'object') return '';

    Logger.log('[ActionWebhook] Reconstructing template from:', { reqBodyObj, mapper });

    // Clone the object to avoid mutation
    const reconstructed = JSON.parse(JSON.stringify(reqBodyObj));

    // For each mapper entry, replace the value with {{fieldName}}
    // mapper format: { "phone": "caller1" } means CRM field "phone" maps to webhook param "caller1"
    Object.entries(mapper).forEach(([crmField, webhookParam]) => {
      Logger.log(`[ActionWebhook] Looking for webhook param "${webhookParam}" to replace with {{${crmField}}}`);
      // Recursively find and replace the webhookParam with {{crmField}}
      replaceInObject(reconstructed, webhookParam, `{{${crmField}}}`);
    });

    const result = JSON.stringify(reconstructed, null, 2);
    Logger.log('[ActionWebhook] Reconstructed template:', result);
    return result;
  };

  // Helper to recursively replace in object (searches both keys and values)
  const replaceInObject = (obj: any, searchKey: string, replaceValue: string) => {
    for (const key in obj) {
      // If the key matches, replace its value with the placeholder
      if (key === searchKey) {
        obj[key] = replaceValue;
      }
      // If the value matches the search key, replace it
      else if (obj[key] === searchKey) {
        obj[key] = replaceValue;
      }
      // Recursively search nested objects
      else if (typeof obj[key] === 'object' && obj[key] !== null) {
        replaceInObject(obj[key], searchKey, replaceValue);
      }
    }
  };

  const handleWebhookSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const isFormValid = await schema.isValid(webhookData, {
      abortEarly: false,
    });

    if (isFormValid) {
      // Convert header entries to Record<string, string>
      const reqHeader = headersToRecord();

      // Auto-generate mapper with ALL lead fields from customForm
      const mapper = generateMapper(webhookData.reqBody);

      Logger.log('[ActionWebhook] Generated mapper with all lead fields:', {
        totalFields: Object.keys(mapper).length,
        fields: Object.keys(mapper),
        mapper
      });

      // Build the final object - only include fields that have values
      const tempObj: any = {
        webhookUrl: webhookData.webhookUrl,
        requestMethod: webhookData.requestMethod,
        reqHeader,
        mapper
      };

      // Only add webhookServiceType if it has a value
      if (webhookData.webhookServiceType) {
        tempObj.webhookServiceType = webhookData.webhookServiceType;
      }

      // Only add reqBody if it has content - parse JSON string to object
      if (webhookData.reqBody) {
        try {
          tempObj.reqBody = JSON.parse(webhookData.reqBody);
        } catch (e) {
          // If not valid JSON, save as string (for backward compatibility)
          tempObj.reqBody = webhookData.reqBody;
        }
      }

      Logger.log('[ActionWebhook] Webhook data being saved:', tempObj);
      Logger.log('[ActionWebhook] Breakdown:', {
        url: tempObj.webhookUrl,
        method: tempObj.requestMethod,
        headerCount: Object.keys(tempObj.reqHeader).length,
        mapperFieldCount: Object.keys(tempObj.mapper).length,
        reqBodyType: typeof tempObj.reqBody,
        reqBodyKeys: tempObj.reqBody ? Object.keys(tempObj.reqBody) : []
      });

      onSaveDrawerClose(tempObj);
    } else {
      schema.validate(webhookData, { abortEarly: false }).catch((err) => {
        const errors = err.inner.reduce(
          (acc: any, error: { path: string; message: string }) => {
            return {
              ...acc,
              [error.path]: error.message,
            };
          },
          {}
        );
        setErrors((prevErrors) =>
          update(prevErrors, {
            $set: errors,
          })
        );
      });
    }
  };

  return (
    <>
      <div className="popup-section-1 popup-section-2 popup-section-3">
        <div>
          <div className="form-group">
            <label className="form-label">Webhook URL (HTTPS only)</label>
            <input
              type="text"
              value={webhookData?.webhookUrl}
              onChange={handleChange}
              name="webhookUrl"
              className="form-control"
              placeholder="https://api.example.com/webhook"
            />
            {errors.webhookUrl ? <ErrorText message={errors?.webhookUrl} /> : ""}
          </div>

          <div className="form-group">
            <label className="form-label">Service Type (Optional)</label>
            <select
              value={webhookData?.webhookServiceType}
              onChange={handleChange}
              name="webhookServiceType"
              className="form-select"
            >
              <option value="">-- Select Service Type (Optional) --</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="custom">Custom</option>
            </select>
            <small className="form-text text-muted">
              Optional: Specify the webhook service type if needed
            </small>
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label className="form-label" style={{ marginBottom: 0 }}>Request Headers</label>
              <button
                type="button"
                onClick={addHeaderEntry}
                className="btn btn-outline-primary btn-sm"
              >
                + Add Header
              </button>
            </div>
            <div style={{ border: '1px solid #e0e0e0', borderRadius: '4px', padding: '12px' }}>
              {headerEntries.map((header, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Header Key (e.g., Authorization)"
                      value={header.key}
                      onChange={(e) => updateHeaderEntry(index, 'key', e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Header Value (e.g., Bearer token123)"
                      value={header.value}
                      onChange={(e) => updateHeaderEntry(index, 'value', e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeHeaderEntry(index)}
                    className="btn btn-outline-danger btn-sm"
                    style={{ whiteSpace: 'nowrap', height: '38px' }}
                    disabled={headerEntries.length === 1}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <small className="form-text text-muted">
              Add custom headers for authentication (e.g., Authorization: Bearer token) or other API requirements
            </small>
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label className="form-label" style={{ marginBottom: 0 }}>Request Body (JSON) - Optional</label>
              <button
                type="button"
                onClick={() => setAssignLeadDrawer(true)}
                className="btn btn-outline-primary btn-sm"
              >
                Insert Lead Field
              </button>
            </div>
            <div style={{ position: 'relative' }}>
              <textarea
                ref={bodyRef}
                className="form-control"
                value={webhookData?.reqBody}
                onChange={handleChange}
                name="reqBody"
                style={{ height: "200px", width: "100%" }}
                placeholder='{"email": "{{email}}", "phone": "{{phone}}", "subscribed": true, "tags": ["customer"]}'
              ></textarea>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginTop: '4px' }}>
                <span>Use <strong>{`{{fieldName}}`}</strong> for dynamic lead data. Other fields are sent as-is.</span>
                <span>{webhookData.reqBody.length}/2000</span>
              </div>
            </div>
            {errors.reqBody ? <ErrorText message={errors?.reqBody} /> : ""}
          </div>
        </div>

        <div className="auto_form_btn">
          <button
            type="button"
            onClick={handleWebhookSubmit}
            className="btn btn-primary-save"
          >
            SAVE
          </button>
        </div>
      </div>

      <GlobalDrawer
        openWithHeader={assignLeadDrawer}
        setOpenWithHeader={() => setAssignLeadDrawer(false)}
        drawerTitle={"Insert Lead Field"}
        size="xs"
      >
        <div>
          {teamMembers?.map((item: TeamMember, ix: number) => {
            return (
              <button
                key={ix}
                onClick={(e) => {
                  e.preventDefault();
                  insertLeadField(item);
                }}
                className="d-block bg-white w-100 p-3 rounded mb-2"
                style={{ margin: "8px 0px", textAlign: "left" }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </GlobalDrawer>
    </>
  );
};

export default ActionWebhook;